import { forwardRef, useCallback } from 'react';
import type { ProductListing } from '../../types/products';
import { ProductCard } from './ProductCard';
import type { ProductCardHandle } from '../../hooks/useGridKeyboardNav';

interface ProductGridProps {
  products: ProductListing[];
  onAddToCart: (productId: number, formatId: number, quantity: number) => Promise<void>;
  isLoadingMore: boolean;
  hasMore: boolean;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  isSearching: boolean;
  searchTotal?: number;
  focusedIndex?: number | null;
  cardHandlesRef?: React.MutableRefObject<Map<number, ProductCardHandle>>;
  onInnerInputFocusChange?: (focused: boolean) => void;
  hasUnavailableMatches?: boolean;
}

export const ProductGrid = forwardRef<HTMLDivElement, ProductGridProps>(
  function ProductGrid({
    products,
    onAddToCart,
    isLoadingMore,
    hasMore,
    sentinelRef,
    isSearching,
    searchTotal,
    focusedIndex,
    cardHandlesRef,
    onInnerInputFocusChange,
    hasUnavailableMatches,
  }, ref) {
    const setCardRef = useCallback((index: number, handle: ProductCardHandle | null) => {
      if (!cardHandlesRef) return;
      if (handle) {
        cardHandlesRef.current.set(index, handle);
      } else {
        cardHandlesRef.current.delete(index);
      }
    }, [cardHandlesRef]);

    if (products.length === 0 && !isLoadingMore) {
      // If parent is rendering unavailable matches UI, suppress the generic empty state
      if (hasUnavailableMatches) return null;

      return (
        <div className="py-16 text-center text-gray-500">
          <i className="fa fa-search mb-3 text-4xl text-gray-300" />
          <p className="text-lg">No se encontraron productos</p>
          {isSearching && (
            <p className="mt-1 text-sm">Probá con otro término de búsqueda</p>
          )}
        </div>
      );
    }

    return (
      <>
        {isSearching && searchTotal !== undefined && searchTotal > 0 && (
          <p className="mb-3 text-sm text-gray-500">
            {searchTotal} {searchTotal === 1 ? 'producto encontrado' : 'productos encontrados'}
          </p>
        )}

        <div ref={ref} className="grid grid-cols-1 gap-3 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {products.map((product, index) => (
            <ProductCard
              key={product.id}
              ref={(handle) => setCardRef(index, handle)}
              product={product}
              onAddToCart={onAddToCart}
              isFocused={focusedIndex === index}
              onInnerInputFocusChange={onInnerInputFocusChange}
            />
          ))}
        </div>

        {/* Loading sentinel for infinite scroll */}
        {hasMore && (
          <div ref={sentinelRef} className="flex justify-center py-8">
            {isLoadingMore && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <i className="fa fa-spinner fa-spin" />
                Cargando más productos...
              </div>
            )}
          </div>
        )}
      </>
    );
  }
);
